<template>
  <div class="ac-body-container">
    <div id="put-table-container" class="i-c-item mt-20">
      <div class="flex-row align-center">
        <div class="sac__font-18 semi-bold">
          Active Subscriptions
        </div>
        <div class="sac__font-16 ml-12 n50 medium">
          Change payment methods for specific subscriptions here.
          The branches billed can be updated in <a @click="change('agreements')">Agreements</a>.
        </div>
      </div>
      <div class="mt-4 put-container scroll-controll">
        <v-data-table
          :headers="subsTableHeaders"
          :items="subscriptions"
          :loading="loadingSubscriptions"
          :search="subtableSearch"
          fixed-header
          hide-default-footer
        >
          <template #[`item.created_at`]="{ item }">
            <td>
              {{
                nextDate(item.created_at)
              }}
            </td>
          </template>
          <template #[`item.plan`]="{ item }">
            <td>
              {{ planName(item.subscription_items[0].item_price_id) }}
            </td>
          </template>
          <template #[`item.branch`]="{ item }">
            <td>
              {{ item.meta_data?.CORP_NAME || "--" }}
            </td>
          </template>
          <template #[`item.amount`]="{ item }">
            <td>
              ${{ (item.subscription_items.map((x) => x.amount)
                .reduce((a, b) => a + b, 0) / 100).toFixed(2) }}
            </td>
          </template>
          <template #[`item.next_billing_at`]="{ item }">
            <td>
              {{ nextDate(item.next_billing_at) }}
            </td>
          </template>
          <template v-if="adminRole === 'admin'" #[`item.actions`]="{ item }">
            <td align="end">
              <button
                type="button"
                @click="download(item.id)"
              >
                <i class="icon-Cloud-Download sac__font-18" />
              </button>
            </td>
          </template>
        </v-data-table>
      </div>
    </div>
    <div v-if="screen !== 'partnerDetail'" class="i-c-item margin-top-16">
      <div class="flex-row align-center">
        <div class="sac__font-18 semi-bold">
          Payment Methods
        </div>
        <div class="sac__font-16 ml-12 n50 medium">
          ACH transfers are required for contract agreements.
        </div>
      </div>
      <div class="mt-4">
        <img
          v-if="loadingPaymentMethod"
          src="@shared/assets/img/general/loading.svg"
          alt="loading"
        />
        <div
          v-for="item in selectedPaymentMethod"
          :key="item.id"
          class="pm-button just-between"
        >
          <div class="flex-row flex-1">
            <PaymentMethodIcon :paymentMethod="item" />
            <div v-if="item.bank_account" class="flex-column align-start ml-4">
              <div class="pmi-header">
                {{ item.bank_account.bank_name }}
              </div>
              <div class="pmi-body">
                Ending in... {{ item.bank_account.last4 }}
              </div>
            </div>
            <div v-else-if="item.card" class="flex-column align-start ml-4">
              <div class="pmi-header">
                {{ item.card.brand.toUpperCase() }}
              </div>
              <div class="pmi-body">
                Ending in... {{ item.card.last4 }}
              </div>
            </div>
          </div>
          <div class="flex-row flex-1">
            <input
              aria-label="Use as default"
              name="default-payment"
              type="radio"
              class="i-radio"
              :checked="defaultPayment === item.id"
              @change="onChange('defaultPayment', item.id)"
            >
            <div class="option ml-5">
              <div class="label">
                Use as default
              </div>
            </div>
          </div>
          <button
            v-if="defaultPayment !== item.id
              && subscriptions.find(x => x.payment_source_id == item.id) === undefined"
            type="button"
            @click="removePayment(item.id)"
          >
            <i class="icon-Trash sac__font-25 tomato" />
          </button>
          <div v-else />
        </div>
        <button type="button" class="mt-5 align-center flex-row" @click="toggleActionModal(true, 'new-payment-method')">
          <i class="icon-Plus sac__font-20 mr-2" /> Add Payment Method
        </button>
      </div>
    </div>
    <div class="i-c-item margin-top-16">
      <div class="sac__font-18 semi-bold">
        Tax Status
      </div>
      <div>
        <div class="i-c-container">
          <input
            aria-label="Tax Exempt"
            name="tax-exempt"
            type="radio"
            class="i-radio"
            :checked="taxExempt === false"
            @change="onChange('taxExempt', false)"
          >
          <div class="option">
            <div class="label">
              I do not have an exemption or resale certificate
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="i-c-container">
          <input
            aria-label="Tax Exempt"
            name="tax-exempt"
            type="radio"
            class="i-radio"
            :checked="taxExempt"
            @change="onChange('taxExempt', true)"
          >
          <div class="option">
            <div class="label">
              My company is exempt from sales tax on SmartAC.com product sales
            </div>
          </div>
        </div>
        <div
          v-if="taxExempt"
          class="ac-tax-container margin-left-8"
        >
          <div class="margin-bottom-4">
            <div class="header">
              Category
            </div>
            <SACDropdown
              searchParameter="reason"
              field="reasonCode"
              placeholder="Select Category"
              :options="categoryList"
              optionValue="code"
              optionLabel="reason"
              :value="reasonCode"
              :change="onChange"
              :error="errorObj.exemptReasonErr"
            />
          </div>
          <div class="margin-bottom-4">
            <div class="header">
              Enter Tax Number
            </div>
            <SACInput
              field="taxNumber"
              placeholder="Enter tax number"
              :value="taxNumber"
              :change="onChange"
              :error="errorObj.exemptDocErr"
            />
          </div>
          <div class="margin-bottom-4">
            <div class="header">
              Enter Expiry Date
            </div>
            <Datepicker v-model="selectedDate" class="input-date" :min-date="new Date()" />
          </div>
          <iframe
            v-if="tempPdfFile || existingPdfFile"
            :title="pdfFileName"
            :src="tempPdfFile || existingPdfFile"
            class="pdf-view"
            type="application/pdf"
          />
          <div class="margin-bottom-4">
            <form
              v-if="!pdfFileName"
              class="dropbox-form"
              enctype="multipart/form-data"
            >
              <input
                ref="pdfRef"
                aria-label="Upload PDF"
                type="file"
                class="pdf-input"
                accept="application/pdf"
                @change="onLoad"
              >
            </form>
            <div class="i-row-container">
              <button type="button" class="i-form-button" @click="selectPdf">
                {{ pdfFileName ? pdfFileName : 'Upload Tax Document' }}
              </button>
              <v-icon v-if="pdfFileName" color="#FF5B52" dark medium @click="deleteFile">
                mdi-trash-can-outline
              </v-icon>
            </div>
            <div class="error-text">
              {{ errorObj.taxDocErr }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="put-table-container" class="i-c-item margin-top-16">
      <div class="flex-row just-between">
        <div class="sac__font-18 semi-bold">
          Invoices
        </div>
      </div>
      <div>
        <div class="sac__font-16 semi-bold margin-top-12">
          Invoice Memo (optional)
        </div>
        <div class="sac__font-14 medium mt-2">
          Add all required details that will be listed on your invoice.
        </div>
        <SACInput
          class="mt-5"
          type="textarea"
          field="invoiceNote"
          placeholder="Such as billing address, company name, VAT number, etc."
          :value="invoiceNote"
          :change="onChange"
          :error="errorObj.invoiceNoteErr"
        />
        <div class="sac__font-16 semi-bold mt-5">
          Send invoices to
        </div>
        <div class="flex-row">
          <SACInput
            class="mt-2 mr-4"
            style="width: 30%;"
            field="invoiceEmail"
            placeholder="Enter email"
            :value="invoiceEmail"
            :change="onChangeField"
          />
          <button type="button" class="add-email-btn mt-2" @click="addEmail">
            Add
            <i class="icon-Plus sac__font-16 ml-2" />
          </button>
        </div>
        <div v-if="invoiceEmailErr" class="sac__font-16 tomato">
          {{ invoiceEmailErr }}
        </div>
        <div
          v-for="item in chargebeeCustomer.contacts"
          :key="item.email"
          class="flex-row mt-5"
        >
          <div class="sac__font-18 mr-4">
            {{ item.email }}
          </div>
          <button
            type="button"
            @click="removeContact(item.id)"
          >
            <i class="icon-Trash sac__font-25 tomato" />
          </button>
        </div>
      </div>
      <div class="flex-row just-end">
        <SACTableSearch :search="tableSearch" @input="(search) => { tableSearch = search }" />
      </div>
      <div class="mt-4 put-container">
        <v-data-table
          :headers="tableHeaders"
          :items="recentInvoices"
          :loading="loadingInvoices"
          :search="tableSearch"
          fixed-header
          hide-default-footer
        >
          <template #[`item.date`]="{ item }">
            <td>
              {{ nextDate(item.date) }}
            </td>
          </template>
          <template #[`item.name`]="{ item }">
            <td>
              {{
                item.firstName && item.lastName
                  ? `${item.firstName} ${item.lastName}`
                  : '--'
              }}
            </td>
          </template>
          <template #[`item.id`]="{ item }">
            <td>
              <a
                v-if="isSmacAdmin"
                :href="`https://smartac.chargebee.com/d/invoices/${item.id}`"
              >
                {{ item.id }}
              </a>
              <span v-else>
                {{ item.id }}
              </span>
            </td>
          </template>
          <template #[`item.total`]="{ item }">
            <td>
              ${{ (item.total / 100).toFixed(2) }}
              <button
                type="button"
                class="margin-left-8"
                @click="download(item.id)"
              >
                <i class="icon-Cloud-Download sac__font-18" />
              </button>
              <button
                v-if="item.hasAddendum"
                type="button"
                class="margin-left-8"
                @click="downloadAddendum(item.id)"
              >
                <i class="icon-luc-FileText sac__font-18" />
              </button>
            </td>
          </template>
          <template #[`item.status`]="{ item }">
            <td>
              {{ (item.status[0].toUpperCase() + item.status.slice(1)).replace('_', ' ') }}
            </td>
          </template>
        </v-data-table>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from 'v-calendar/lib/components/date-picker.umd';
import moment from 'moment';
import settingMixin from '@shared/mixins/settingMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import emailValidation from '@shared/lib/errorCatch/emailValidation';

import SACInput from '@shared/components/sac/SACInput.vue';
import SACDropdown from '../../sac/SACDropdown.vue';
import PaymentMethodIcon from '../../shared/PaymentMethodIcon.vue';
import SACTableSearch from '../../table/SACTableSearch.vue';

import exportToFile from '@shared/lib/util/exportToFile';

export default {
  name: 'BillingProfile',
  components: {
    SACDropdown,
    SACInput,
    SACTableSearch,
    Datepicker,
    PaymentMethodIcon,
  },
  mixins: [settingMixin, unitPreferencesMixin],
  props: {
    screen: String,
    taxExempt: {
      type: Boolean,
      default: false,
    },
    corpId: Number,
    pdfFileName: String,
    taxNumber: String,
    defaultPayment: String,
    expireDate: String,
    reasonCode: String,
    taxDoc: [Blob, String],
    taxDocUrl: String,
    categoryList: Array,
    invoiceNote: String,
    change: Function,
    onChange: Function,
  },
  data() {
    return {
      existingPdfFile: null,
      tempPdfFile: null,
      tableSearch: '',
      subtableSearch: '',
      invoiceEmail: '',
      invoiceEmailErr: '',
    };
  },
  computed: {
    selectedDate: {
      get() {
        const date = moment(this.expireDate).format('MM/DD/YYYY');
        return new Date(date);
      },
      set(val) {
        this.onChange('expireDate', moment(val).format('YYYY-MM-DD'));
      },
    },
    subsTableHeaders() {
      const headers = [{
        text: 'Start Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'created_at',
      }, {
        text: 'Branch',
        sortable: true,
        filterable: true,
        value: 'branch',
      }, {
        text: 'Plan',
        sortable: true,
        filterable: true,
        value: 'plan',
      }, {
        text: 'Amount Due',
        sortable: true,
        filterable: true,
        value: 'amount',
      }, {
        text: 'Due date',
        sortable: true,
        filterable: true,
        value: 'next_billing_at',
      }];
      return headers;
    },
    tableHeaders() {
      const headers = [{
        text: 'Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'date',
      }, {
        text: 'Invoice',
        sortable: true,
        filterable: true,
        value: 'id',
      }, {
        text: 'Payment Status',
        sortable: true,
        filterable: true,
        value: 'status',
      }, {
        text: 'Amount',
        sortable: true,
        filterable: true,
        value: 'total',
      }];
      return headers;
    },
  },
  watch: {
    taxDoc() {
      this.renderTempPdf();
    },
  },
  async mounted() {
    if (this.taxDocUrl) {
      await this.loadingPdf();
    } else if (this.taxDoc) {
      this.renderTempPdf();
    }
    await this.$store.dispatch('getRecentInvoicesMethod', this.corpId);
    await this.$store.dispatch('getSubscriptions', this.corpId);
  },
  methods: {
    renderTempPdf() {
      if (!this.taxDoc) {
        this.tempPdfFile = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result.split(',')[1];
        this.tempPdfFile = `data:application/pdf;base64,${base64String}`;
      };
      reader.readAsDataURL(this.taxDoc);
    },
    onChangeField(field, value) {
      this[field] = value;
    },
    addEmail() {
      this.invoiceEmailErr = '';
      if (this.chargebeeCustomer.contacts?.find((x) => x.email === this.invoiceEmail)) {
        this.invoiceEmailErr = 'Email already exists';
      } else if (emailValidation(this.invoiceEmail)) {
        this.$store.dispatch('createChargebeeContact', { invoiceEmail: this.invoiceEmail, corpId: this.corpId });
        this.invoiceEmail = '';
      } else {
        this.invoiceEmailErr = 'Enter Email';
      }
    },
    nextDate(unix) {
      return moment.unix(unix).format('MM/DD/yy');
    },
    planName(id) {
      return this?.allProducts.find((x) => x.priceId === id).itemName;
    },
    async download(id) {
      const { url } = await this.$store.dispatch('getInvoicePdf', id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${id}`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    async downloadAddendum(id) {
      const addendum = await this.$store.dispatch('getInvoiceAddendum', id);
      const payload = {
        data: addendum,
        filename: `${id}-vouchers.csv`,
      };

      exportToFile(payload);
    },
    removePayment(id) {
      this.changeSettingValues([{
        field: 'paymentMethodId',
        value: id,
      }]);
      this.toggleActionModal(true, 'warning-payment');
    },
    removeContact(id) {
      this.changeSettingValues([{
        field: 'chargebeeContactId',
        value: id,
      }]);
      this.toggleActionModal(true, 'warning-chargebee-contact');
    },
    onLoad(e) {
      this.onChange('taxDoc', e.target.files[0]);
      this.onChange('pdfFileName', e.target.files[0].name);
    },
    selectPdf() {
      const ref = this.$refs.pdfRef;
      if (ref) {
        ref.click();
      }
    },
    deleteFile() {
      this.onChange('taxDoc', null);
      this.onChange('pdfFileName', null);
    },
    async loadingPdf() {
      this.existingPdfFile = await this.$store.dispatch('getDocResponse', {
        url: this.taxDocUrl,
      });
    },
  },
};
</script>

<style lang="scss">
.header {
  font-size: 1rem;
  font-weight: 700;
  color: $N80;
}

.pm-button {
  padding: 8px 12px;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 1fr 25px;
  align-items: center;

  .pmi-header {
    font-size: 1rem;
    font-weight: 700;
    color: $N90;
  }

  .pmi-body {
    font-size: 0.875rem;
    color: $N80;
    font-weight: 500;
  }

  .pmi-edit {
    margin-left: 18px;
    color: $NBLUE100;
    font-weight: 700;
  }
}

.ac-tax-container {
  width: 50%;

  &:not(:last-child) {
    padding-bottom: 18px;
    margin-bottom: 18px;
    border-bottom: 2px solid $N20;
  }

  .pdf-input {
    display: none;
  }

  .pdf-view {
    height: 400px;
    width: 100%;
    border: 0;
  }

  .input-date {
    width: 100%;
    height: 48px;
    border-radius: 2px;
    background-color: $NBLUE5;
  }

  .i-row-container {
    display: grid;
    grid-auto-flow: column;
    align-items: flex-end;
    gap: 10px;
    justify-content: flex-start;

    .i-form-button {
      padding: 0 16px;
      height: 40px;
      border-radius: 6px;
      background-color: $NBLUE100;
      font-weight: 600;
      color: $WHITE;
    }
  }

  .error-text {
    color: $TOMATO100;
    font-weight: 500;
    font-size: 14px;
    margin-top: 5px;
  }
}

.i-c-container {
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-start;
  gap: 10px;
  align-items: baseline;
  padding: 20px 5px 0 5px;

  .i-radio {
    transform: scale(1.5);
  }

  .option {
    .label {
      font-size: 1rem;
      font-weight: 600;
      color: $N70;
    }
  }
}

.body {
  font-size: 0.875rem;
  margin-top: 8px;
  margin-bottom: 8px;
  color: $N80;
}

.i-c-item {
  border: $N40 1px solid;
  padding: 17px;
  border-radius: 8px;
}

#put-table-container {
  width: 100%;

  .pu-header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;

    .create-btn {
      padding: 0 16px;
      height: 40px;
      border-radius: 6px;
      background-color: $NBLUE100;
      color: $WHITE;
      font-weight: 600;
      margin-right: 24px;

      &:hover {
        background-color: $NBLUE90;
      }
    }
  }
  .scroll-controll {
    max-height: 450px;
    overflow-y: auto;
  }
  .put-container {
    background-color: $WHITE;
    box-shadow: 0 0 7px 2px rgba(9, 15, 32, 0.02);
    border-radius: 6px;
    padding-bottom: 12px;

    .v-data-table__wrapper {

      thead {
        tr {
          th {
            background-color: $N100;
            color: $WHITE;
            font-size: 15px;
            font-weight: 500;
            text-transform: uppercase;

            .mdi-checkbox-blank-outline::before,
            .mdi-checkbox-marked::before,
            .mdi-minus-box::before,
            .mdi-arrow-up::before {
              color: $WHITE;
            }

            &:first-child {
              border-top-left-radius: 6px;
            }

            &:last-child {
              border-top-right-radius: 6px;
            }

            &.sortable {
              i {
                display: none;
              }
            }

            &.active {
              i {
                display: inline-block;
              }
            }
          }
        }
      }

      tbody {
        tr {
          td {
            border-bottom: 0;

            .mdi-checkbox-marked::before {
              color: $NBLUE100;
            }

            .edit-btn {
              background: $N90;
              color: $WHITE;
              width: 70px;
              padding: 3px;
              border-radius: 6px;
            }
          }

          &:hover {
            background: none;
          }

          &:nth-child(even) {
            background-color: $NBLUE5;
          }
        }
      }
    }
  }

}
.add-email-btn {
  background-color: #2766E4;
  padding: 10px 15px;
  align-items: center;
  display: flex;
  flex-direction: row;
  color: white;
  border-radius: 4px;
  font-size: 14px;
}

</style>
